import React, { useEffect, useState } from 'react';
import { Button, Input, Loader, PageHead, Select, Switch, Textarea } from '../components/ui';
import { settingsApi } from '../api';
import { API_BASE } from '../api/client';
import { useToast } from '../context/ToastContext';

/**
 * Every third-party credential the platform uses is edited here and stored in
 * MongoDB — nothing lives in .env. Saving takes effect on the next API call,
 * so keys can be rotated and OTP flipped dev↔live without a redeploy.
 */

const Section = ({ id, icon, title, description, open, onToggle, enabled, onEnabledChange, children }) => (
  <div className="setting-section">
    <div className={`setting-head ${open ? 'open' : ''}`} onClick={() => onToggle(open ? null : id)}>
      <span className="s-ico">{icon}</span>
      <div className="grow">
        <div className="s-name">{title}</div>
        <div className="s-desc">{description}</div>
      </div>
      {onEnabledChange && (
        <div onClick={e => e.stopPropagation()}>
          <Switch checked={enabled} onChange={onEnabledChange} label={enabled ? 'Enabled' : 'Disabled'} />
        </div>
      )}
      <span className="chev">▾</span>
    </div>
    {open && <div className="setting-content">{children}</div>}
  </div>
);

const Settings = () => {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState('otp');
  const [testing, setTesting] = useState('');
  const [testMobile, setTestMobile] = useState('');
  const [testEmail, setTestEmail] = useState('');

  const load = () => {
    settingsApi
      .get()
      .then(res => setSettings(res.data))
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Local edit — nothing hits the API until Save. */
  const set = (group, key, value) =>
    setSettings(prev => ({ ...prev, [group]: { ...prev[group], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { _id, key, createdAt, updatedAt, __v, updatedBy, ...payload } = settings;
      const res = await settingsApi.update(payload);
      setSettings(res.data);
      toast.success('Settings saved — changes are live immediately');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const flipOtpMode = async mode => {
    try {
      const res = await settingsApi.setOtpMode({ mode, devOtp: settings.otp.devOtp });
      setSettings(prev => ({ ...prev, otp: res.data.otp }));
      toast.success(res.message);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const runTest = async (which) => {
    setTesting(which);
    try {
      let res;
      if (which === 'payment') res = await settingsApi.testPayment();
      if (which === 'whatsapp') res = await settingsApi.testWhatsapp(testMobile);
      if (which === 'email') res = await settingsApi.testEmail(testEmail);
      if (which === 'delhivery') res = await settingsApi.testDelhivery();
      toast.success(res.message);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTesting('');
    }
  };

  if (loading) return <Loader label="Loading platform settings…" />;
  if (!settings) return null;

  const devMode = settings.otp.mode === 'dev';

  return (
    <>
      <PageHead
        title="Platform settings"
        subtitle="API keys, gateways and platform behaviour — stored in the database, applied instantly."
        actions={
          <>
            <Button variant="ghost" onClick={load}>Reload</Button>
            <Button loading={saving} onClick={save} icon="✓">Save all changes</Button>
          </>
        }
      />

      <div className={`mode-banner ${devMode ? 'dev' : 'live'}`}>
        <span className="m-ico">{devMode ? '🧪' : '🔒'}</span>
        <div className="grow">
          <div className="m-title">OTP is in {devMode ? 'DEVELOPMENT' : 'LIVE'} mode</div>
          <div className="m-desc">
            {devMode
              ? `Any mobile number can sign in with the code ${settings.otp.devOtp}. No SMS or WhatsApp message is sent.`
              : 'Real one-time passwords are generated and delivered through the configured gateway.'}
          </div>
        </div>
        <Button variant={devMode ? 'primary' : 'ghost'} onClick={() => flipOtpMode(devMode ? 'live' : 'dev')}>
          Switch to {devMode ? 'LIVE' : 'DEV'} mode
        </Button>
      </div>

      {/* ----------------------------------------------------------- OTP */}
      <Section
        id="otp" icon="🔑" title="OTP & login"
        description="Development bypass code, expiry, retry limits and delivery channel."
        open={open === 'otp'} onToggle={setOpen}
      >
        <div className="grid-2">
          <Select
            label="Mode" value={settings.otp.mode}
            onChange={e => set('otp', 'mode', e.target.value)}
            options={[
              { value: 'dev', label: 'Development — accept a fixed code' },
              { value: 'live', label: 'Live — send a real OTP' },
            ]}
            hint="Dev mode lets QA log in without an SMS gateway."
          />
          <Input
            label="Development OTP" value={settings.otp.devOtp}
            onChange={e => set('otp', 'devOtp', e.target.value.replace(/\D/g, ''))}
            maxLength={8} className="secret-input"
            hint="Accepted for every number while in dev mode."
          />
          <Input label="OTP length" type="number" min={4} max={8} value={settings.otp.length} onChange={e => set('otp', 'length', Number(e.target.value))} />
          <Input label="Expiry (minutes)" type="number" min={1} value={settings.otp.expiryMinutes} onChange={e => set('otp', 'expiryMinutes', Number(e.target.value))} />
          <Input label="Resend cooldown (seconds)" type="number" min={0} value={settings.otp.resendCooldownSeconds} onChange={e => set('otp', 'resendCooldownSeconds', Number(e.target.value))} />
          <Input label="Max wrong attempts" type="number" min={1} value={settings.otp.maxAttempts} onChange={e => set('otp', 'maxAttempts', Number(e.target.value))} />
          <Select
            label="Delivery channel" value={settings.otp.channel}
            onChange={e => set('otp', 'channel', e.target.value)}
            options={[
              { value: 'sms', label: 'SMS only' },
              { value: 'whatsapp', label: 'WhatsApp only' },
              { value: 'both', label: 'SMS + WhatsApp' },
            ]}
          />
        </div>
      </Section>

      {/* ------------------------------------------------------- Razorpay */}
      <Section
        id="razorpay" icon="₹" title="Razorpay payment gateway"
        description="Keys used for online order payments and refunds."
        open={open === 'razorpay'} onToggle={setOpen}
        enabled={settings.razorpay.enabled} onEnabledChange={v => set('razorpay', 'enabled', v)}
      >
        <div className="grid-2">
          <Input label="Key ID" value={settings.razorpay.keyId} onChange={e => set('razorpay', 'keyId', e.target.value)} placeholder="rzp_live_xxxxxxxxxxxx" className="secret-input" hint="Safe to expose — the app reads this to open checkout." />
          <Input label="Key secret" type="password" value={settings.razorpay.keySecret} onChange={e => set('razorpay', 'keySecret', e.target.value)} placeholder="••••••••••••••••" className="secret-input" hint="Never sent to the app. Used server-side only." />
          <Input label="Webhook secret" type="password" value={settings.razorpay.webhookSecret} onChange={e => set('razorpay', 'webhookSecret', e.target.value)} placeholder="••••••••" className="secret-input" hint="Set the same value in the Razorpay dashboard webhook." />
          <Input label="Currency" value={settings.razorpay.currency} onChange={e => set('razorpay', 'currency', e.target.value.toUpperCase())} maxLength={3} />
        </div>
        <div className="row">
          <Button variant="soft" loading={testing === 'payment'} onClick={() => runTest('payment')}>Test these keys</Button>
          <span className="text-muted text-sm">Save first, then test — the check reads the stored keys.</span>
        </div>
        <div className="hint mt-2">
          Webhook URL: <code>{`${API_BASE}/payments/webhook`}</code>
        </div>
      </Section>

      {/* ------------------------------------------------------- WhatsApp */}
      <Section
        id="whatsapp" icon="💬" title="WhatsApp Business API"
        description="Order, authorization and claim alerts sent over WhatsApp."
        open={open === 'whatsapp'} onToggle={setOpen}
        enabled={settings.whatsapp.enabled} onEnabledChange={v => set('whatsapp', 'enabled', v)}
      >
        <div className="grid-2">
          <Select
            label="Provider" value={settings.whatsapp.provider}
            onChange={e => set('whatsapp', 'provider', e.target.value)}
            options={[
              { value: 'meta', label: 'Meta Cloud API (official)' },
              { value: 'gupshup', label: 'Gupshup' },
              { value: 'custom', label: 'Custom gateway' },
            ]}
          />
          <Input label="API URL" value={settings.whatsapp.apiUrl} onChange={e => set('whatsapp', 'apiUrl', e.target.value)} placeholder="https://graph.facebook.com/v21.0" />
          <Input label="Access token" type="password" value={settings.whatsapp.token} onChange={e => set('whatsapp', 'token', e.target.value)} className="secret-input" placeholder="EAAG..." />
          <Input label="Phone number ID" value={settings.whatsapp.phoneNumberId} onChange={e => set('whatsapp', 'phoneNumberId', e.target.value)} hint="Meta Cloud API only." />
          <Input label="Sender number" value={settings.whatsapp.from} onChange={e => set('whatsapp', 'from', e.target.value)} placeholder="919812345678" />
          <Input label="OTP template name" value={settings.whatsapp.otpTemplate} onChange={e => set('whatsapp', 'otpTemplate', e.target.value)} />
        </div>
        <div className="row wrap">
          <div style={{ maxWidth: 220 }}>
            <Input label="Send a test to" lead="+91" value={testMobile} onChange={e => setTestMobile(e.target.value.replace(/\D/g, ''))} maxLength={10} placeholder="9876543210" />
          </div>
          <Button variant="soft" loading={testing === 'whatsapp'} onClick={() => runTest('whatsapp')}>Send test message</Button>
        </div>
      </Section>

      {/* ------------------------------------------------------------ SMS */}
      <Section
        id="sms" icon="✉" title="SMS gateway"
        description="Used to deliver OTPs when live mode is on."
        open={open === 'sms'} onToggle={setOpen}
        enabled={settings.sms.enabled} onEnabledChange={v => set('sms', 'enabled', v)}
      >
        <div className="grid-2">
          <Input label="Provider name" value={settings.sms.provider} onChange={e => set('sms', 'provider', e.target.value)} placeholder="msg91 / textlocal / custom" />
          <Input label="API URL" value={settings.sms.apiUrl} onChange={e => set('sms', 'apiUrl', e.target.value)} placeholder="https://api.provider.com/send" />
          <Input label="API key" type="password" value={settings.sms.apiKey} onChange={e => set('sms', 'apiKey', e.target.value)} className="secret-input" />
          <Input label="Sender ID" value={settings.sms.senderId} onChange={e => set('sms', 'senderId', e.target.value.toUpperCase())} maxLength={6} hint="6-character DLT-approved header." />
          <Input label="DLT template ID" value={settings.sms.templateId} onChange={e => set('sms', 'templateId', e.target.value)} />
        </div>
      </Section>

      {/* ----------------------------------------------------------- SMTP */}
      <Section
        id="email" icon="📧" title="Email (SMTP)"
        description="Invoices, verification results and partner notifications."
        open={open === 'email'} onToggle={setOpen}
        enabled={settings.email.enabled} onEnabledChange={v => set('email', 'enabled', v)}
      >
        <div className="grid-2">
          <Input label="SMTP host" value={settings.email.host} onChange={e => set('email', 'host', e.target.value)} placeholder="smtp.gmail.com" />
          <Input label="Port" type="number" value={settings.email.port} onChange={e => set('email', 'port', Number(e.target.value))} />
          <Input label="Username" value={settings.email.user} onChange={e => set('email', 'user', e.target.value)} />
          <Input label="Password" type="password" value={settings.email.pass} onChange={e => set('email', 'pass', e.target.value)} className="secret-input" />
          <Input label="From name" value={settings.email.fromName} onChange={e => set('email', 'fromName', e.target.value)} />
          <Input label="From email" value={settings.email.fromEmail} onChange={e => set('email', 'fromEmail', e.target.value)} />
        </div>
        <Switch checked={settings.email.secure} onChange={v => set('email', 'secure', v)} label="Use TLS/SSL (port 465)" />
        <div className="row wrap mt-2">
          <div style={{ maxWidth: 260 }}>
            <Input label="Send a test to" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@company.com" />
          </div>
          <Button variant="soft" loading={testing === 'email'} onClick={() => runTest('email')}>Send test email</Button>
        </div>
      </Section>

      {/* ------------------------------------------------------------ FCM */}
      <Section
        id="firebase" icon="🔔" title="Firebase Cloud Messaging"
        description="Push notifications to the Android and iOS apps."
        open={open === 'firebase'} onToggle={setOpen}
        enabled={settings.firebase.enabled} onEnabledChange={v => set('firebase', 'enabled', v)}
      >
        <div className="grid-2">
          <Input label="Server key" type="password" value={settings.firebase.serverKey} onChange={e => set('firebase', 'serverKey', e.target.value)} className="secret-input" hint="Firebase Console → Project settings → Cloud Messaging." />
          <Input label="Project ID" value={settings.firebase.projectId} onChange={e => set('firebase', 'projectId', e.target.value)} />
        </div>
      </Section>

      {/* --------------------------------------------------------- AWS S3 */}
      <Section
        id="aws" icon="☁" title="AWS S3 storage"
        description="Where product images and KYC documents are stored."
        open={open === 'aws'} onToggle={setOpen}
        enabled={settings.aws.enabled} onEnabledChange={v => set('aws', 'enabled', v)}
      >
        <div className="grid-2">
          <Input label="Access key ID" value={settings.aws.accessKeyId} onChange={e => set('aws', 'accessKeyId', e.target.value)} className="secret-input" />
          <Input label="Secret access key" type="password" value={settings.aws.secretAccessKey} onChange={e => set('aws', 'secretAccessKey', e.target.value)} className="secret-input" />
          <Input label="Region" value={settings.aws.region} onChange={e => set('aws', 'region', e.target.value)} placeholder="ap-south-1" />
          <Input label="Bucket" value={settings.aws.bucket} onChange={e => set('aws', 'bucket', e.target.value)} />
          <Input label="CDN / public base URL" value={settings.aws.baseUrl} onChange={e => set('aws', 'baseUrl', e.target.value)} placeholder="https://cdn.tarpex.live" />
        </div>
        <div className="hint">
          While S3 is disabled, uploads are written to the API server's <code>/uploads</code> folder so the
          platform stays usable before credentials are added.
        </div>
      </Section>

      {/* ----------------------------------------------------- Delhivery */}
      <Section
        id="delhivery" icon="🚚" title="Delhivery courier"
        description="Rates, waybills, pickup and live tracking for every shipment."
        open={open === 'delhivery'} onToggle={setOpen}
        enabled={settings.delhivery.enabled} onEnabledChange={v => set('delhivery', 'enabled', v)}
      >
        <div className="grid-2">
          <Input
            label="API token" type="password" className="secret-input"
            value={settings.delhivery.apiToken}
            onChange={e => set('delhivery', 'apiToken', e.target.value)}
            hint="Delhivery One → Settings → API Setup."
          />
          <Input
            label="Client / warehouse name"
            value={settings.delhivery.clientName}
            onChange={e => set('delhivery', 'clientName', e.target.value)}
            hint="Must match the registered client name exactly."
          />
          <Select
            label="Environment" value={settings.delhivery.mode}
            onChange={e => set('delhivery', 'mode', e.target.value)}
            options={[
              { value: 'staging', label: 'Staging — staging-express.delhivery.com' },
              { value: 'production', label: 'Production — track.delhivery.com' },
            ]}
          />
          <Select
            label="Service mode" value={settings.delhivery.serviceMode}
            onChange={e => set('delhivery', 'serviceMode', e.target.value)}
            options={[
              { value: 'S', label: 'Surface — cheaper, slower' },
              { value: 'E', label: 'Express — faster, dearer' },
            ]}
          />
          <Input
            label="Pickup location name"
            value={settings.delhivery.pickupLocationName}
            onChange={e => set('delhivery', 'pickupLocationName', e.target.value)}
            hint="The registered pickup point Delhivery collects from."
          />
          <Input
            label="Default pickup pincode"
            value={settings.delhivery.defaultPickupPincode}
            onChange={e => set('delhivery', 'defaultPickupPincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            hint="Used when a seller's warehouse has no pincode of its own."
          />
          <Input
            label="Handling fee (₹)" type="number"
            value={settings.delhivery.handlingFee}
            onChange={e => set('delhivery', 'handlingFee', Number(e.target.value))}
            hint="Added on top of the courier rate. 0 to pass the rate through as-is."
          />
          <Input
            label="Fallback weight (grams)" type="number"
            value={settings.delhivery.fallbackWeightGrams}
            onChange={e => set('delhivery', 'fallbackWeightGrams', Number(e.target.value))}
            hint="Used when a product has no weight recorded, so a sale is never blocked."
          />
          <Input
            label="Custom base URL (optional)"
            value={settings.delhivery.baseUrl}
            onChange={e => set('delhivery', 'baseUrl', e.target.value)}
            placeholder="Leave blank to use the environment above"
          />
        </div>

        <div className="row">
          <Button variant="soft" loading={testing === 'delhivery'} onClick={() => runTest('delhivery')}>
            Test connection
          </Button>
          <span className="text-muted text-sm">Save first — the check reads the stored token.</span>
        </div>

        <div className="hint mt-2">
          Sellers no longer set their own delivery charges. Shipping is rated by Delhivery from the product
          weight and the destination pincode, and shown to the buyer as a separate line at checkout. Make sure
          every product has a weight — anything without one falls back to the value above.
        </div>
      </Section>

      {/* ---------------------------------------------------------- Maps */}
      <Section
        id="maps" icon="📍" title="Google Maps"
        description="Delivery addresses, warehouse pins and shipment tracking."
        open={open === 'maps'} onToggle={setOpen}
        enabled={settings.maps.enabled} onEnabledChange={v => set('maps', 'enabled', v)}
      >
        <Input label="Google Maps API key" type="password" value={settings.maps.googleMapsKey} onChange={e => set('maps', 'googleMapsKey', e.target.value)} className="secret-input" hint="Enable Maps SDK for Android/iOS and the Places API on this key." />
      </Section>

      {/* ------------------------------------------------- GST verification */}
      <Section
        id="gst" icon="🧾" title="GST verification provider"
        description="Optional third-party GSTIN lookup during company onboarding."
        open={open === 'gst'} onToggle={setOpen}
        enabled={settings.gstVerification.enabled} onEnabledChange={v => set('gstVerification', 'enabled', v)}
      >
        <div className="grid-2">
          <Input label="Provider name" value={settings.gstVerification.providerName} onChange={e => set('gstVerification', 'providerName', e.target.value)} placeholder="Appyflow / KnowYourGST / Sandbox…" />
          <Input label="API URL" value={settings.gstVerification.apiUrl} onChange={e => set('gstVerification', 'apiUrl', e.target.value)} placeholder="https://api.provider.com/gst" />
          <Input label="API key" type="password" value={settings.gstVerification.apiKey} onChange={e => set('gstVerification', 'apiKey', e.target.value)} className="secret-input" />
        </div>

        <div className="card-title mt-2 mb-2">How the provider expects the request</div>
        <div className="grid-2">
          <Select
            label="How is the GSTIN sent?"
            value={settings.gstVerification.requestStyle}
            onChange={e => set('gstVerification', 'requestStyle', e.target.value)}
            options={[
              { value: 'path', label: 'In the URL path — /gst/{GSTIN}' },
              { value: 'query', label: 'As a query parameter — ?gstin={GSTIN}' },
              { value: 'body', label: 'In the POST body' },
            ]}
          />
          <Select
            label="HTTP method"
            value={settings.gstVerification.method}
            onChange={e => set('gstVerification', 'method', e.target.value)}
            options={[{ value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' }]}
          />
          <Input label="Parameter name" value={settings.gstVerification.requestParam} onChange={e => set('gstVerification', 'requestParam', e.target.value)} placeholder="gstin" hint="Used for the query / body styles." />
          <Select
            label="Where does the key go?"
            value={settings.gstVerification.authIn}
            onChange={e => set('gstVerification', 'authIn', e.target.value)}
            options={[
              { value: 'header', label: 'Request header' },
              { value: 'query', label: 'Query parameter' },
              { value: 'none', label: 'No auth' },
            ]}
          />
          <Input label="Key name" value={settings.gstVerification.authKey} onChange={e => set('gstVerification', 'authKey', e.target.value)} placeholder="Authorization / x-api-key" />
          <Input label="Key prefix" value={settings.gstVerification.authPrefix} onChange={e => set('gstVerification', 'authPrefix', e.target.value)} placeholder="Bearer " hint="Leave blank if the key is sent bare." />
        </div>

        <div className="card-title mt-2 mb-2">Where the answer lives in the response</div>
        <div className="grid-2">
          <Input label="Status field" value={settings.gstVerification.statusField} onChange={e => set('gstVerification', 'statusField', e.target.value)} placeholder="data.sts" hint="Dot path. Leave blank to auto-detect the common shapes." />
          <Input label="Legal name field" value={settings.gstVerification.nameField} onChange={e => set('gstVerification', 'nameField', e.target.value)} placeholder="data.lgnm" />
          <Input label="Value that means active" value={settings.gstVerification.activeValue} onChange={e => set('gstVerification', 'activeValue', e.target.value)} placeholder="Active" />
        </div>

        <div className="hint">
          With no provider configured, a GSTIN is still checked offline against its mod-36 check digit — that
          catches typos and invented numbers, but cannot tell you whether the registration is active.
          There is no free official API for active status; providers such as Appyflow, KnowYourGST or
          Sandbox offer free tiers you can plug in above.
        </div>
      </Section>

      {/* ---------------------------------------------------- Commission */}
      <Section
        id="commission" icon="◈" title="Commission"
        description="Platform take recorded against every order."
        open={open === 'commission'} onToggle={setOpen}
      >
        <div className="grid-3">
          <Input label="Default %" type="number" step="0.1" value={settings.commission.defaultPercent} onChange={e => set('commission', 'defaultPercent', Number(e.target.value))} />
          <Input label="Manufacturer %" type="number" step="0.1" value={settings.commission.manufacturerPercent} onChange={e => set('commission', 'manufacturerPercent', Number(e.target.value))} />
          <Input label="Distributor %" type="number" step="0.1" value={settings.commission.distributorPercent} onChange={e => set('commission', 'distributorPercent', Number(e.target.value))} />
        </div>
      </Section>

      {/* ------------------------------------------------------- Business */}
      <Section
        id="business" icon="🏢" title="Business & billing"
        description="Defaults used across invoices, pricing and support."
        open={open === 'business'} onToggle={setOpen}
      >
        <div className="grid-2">
          <Input label="Platform company name" value={settings.business.companyName} onChange={e => set('business', 'companyName', e.target.value)} />
          <Input label="Currency" value={settings.business.currency} onChange={e => set('business', 'currency', e.target.value.toUpperCase())} maxLength={3} />
          <Input label="Default GST rate (%)" type="number" value={settings.business.defaultGstRate} onChange={e => set('business', 'defaultGstRate', Number(e.target.value))} />
          <Input label="Minimum order value" type="number" value={settings.business.minOrderValue} onChange={e => set('business', 'minOrderValue', Number(e.target.value))} />
          <Input
            label="Unverified buyer limit (₹)"
            type="number"
            value={settings.business.unverifiedBuyerLimit}
            onChange={e => set('business', 'unverifiedBuyerLimit', Number(e.target.value))}
            hint="How much a buyer may purchase without an active GSTIN. Set 0 to remove the cap."
          />
          <Select
            label="How the limit is applied"
            value={settings.business.buyerLimitScope}
            onChange={e => set('business', 'buyerLimitScope', e.target.value)}
            options={[
              { value: 'per_order', label: 'Per order — caps each individual order' },
              { value: 'cumulative', label: 'Cumulative — caps lifetime purchases' },
            ]}
          />
          <Input label="Support mobile" value={settings.business.supportMobile} onChange={e => set('business', 'supportMobile', e.target.value)} />
          <Input label="Support email" value={settings.business.supportEmail} onChange={e => set('business', 'supportEmail', e.target.value)} />
        </div>
        <Switch
          checked={settings.business.autoApproveActiveGst}
          onChange={v => set('business', 'autoApproveActiveGst', v)}
          label="Approve companies automatically when their GSTIN is active"
        />
        <div className="hint mb-2">
          An active GSTIN is itself proof of registration, so there is nothing left for you to check —
          the company goes straight to Verified instead of queueing. Buyers with no GSTIN are also active
          immediately, capped by the limit above. Turn this off to review every company by hand.
        </div>

        <Textarea label="Registered address (printed on invoices)" value={settings.business.companyAddress} onChange={e => set('business', 'companyAddress', e.target.value)} rows={2} />
      </Section>

      {/* ------------------------------------------------------------ App */}
      <Section
        id="app" icon="📱" title="Mobile app & maintenance"
        description="Version gating and the maintenance switch for the apps."
        open={open === 'app'} onToggle={setOpen}
      >
        <div className="grid-2">
          <Input label="Latest Android version" value={settings.app.androidVersion} onChange={e => set('app', 'androidVersion', e.target.value)} />
          <Input label="Latest iOS version" value={settings.app.iosVersion} onChange={e => set('app', 'iosVersion', e.target.value)} />
          <Input label="Play Store URL" value={settings.app.androidStoreUrl} onChange={e => set('app', 'androidStoreUrl', e.target.value)} />
          <Input label="App Store URL" value={settings.app.iosStoreUrl} onChange={e => set('app', 'iosStoreUrl', e.target.value)} />
        </div>
        <div className="col" style={{ gap: 12 }}>
          <Switch checked={settings.app.forceUpdate} onChange={v => set('app', 'forceUpdate', v)} label="Force users onto the latest version" />
          <Switch checked={settings.app.maintenanceMode} onChange={v => set('app', 'maintenanceMode', v)} label="Maintenance mode (blocks app access)" />
        </div>
        <Textarea label="Maintenance message" value={settings.app.maintenanceMessage} onChange={e => set('app', 'maintenanceMessage', e.target.value)} rows={2} />
      </Section>

      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
        <Button size="lg" loading={saving} onClick={save} icon="✓">Save all changes</Button>
      </div>
    </>
  );
};

export default Settings;
